import React from "react";
// @ts-ignore
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // Add import!
import { utils as XLSXUtils, writeFile as writeXLSXFile } from "xlsx";
import {
  Document,
  Packer,
  Paragraph,
  Table as DocxTable,
  TableRow,
  TableCell,
} from "docx";
import Papa from "papaparse";
import { FaFilter } from "react-icons/fa";

import {
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnFiltersState,
  SortingState,
} from "@tanstack/react-table";

import { MoreActionsMenu } from "../../atoms/MoreActionsMenu/MoreActionsMenu";
import { PaziaButton } from "../../atoms";
import { Pagination } from "./Pagination";
import { TableHeader } from "./TableHeader";
import { BaseTanTableProps, TableBody, hasObjectWithId } from "./TanTable";
import { TAN_TABLE_CONSTS } from "./TanTable.consts";
import { useIsMobile } from "utils/hooks/useIsMobile";

// Matches ISO-8601 date/datetime strings (what the API sends), e.g. "2025-11-30" or
// "2025-11-30T14:37:00Z". Used to decide whether a string cell value should be export-formatted
// as a date - see getCellExportValue below.
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

interface TanTableClientProps<T> extends BaseTanTableProps<T> {
  pageSize?: number;
  hidePagination?: boolean;
  exportCsv?: boolean;
  csvFields?: string[];
  exportFileName?: string;
  selectOnClick?: boolean;
  filterStorageKey?: string;
  initialFilters?: ColumnFiltersState;
  onClearFilters?: () => void;
  initialSorting?: SortingState;
  renderSubRows?: (rowOriginal: any, colSpan: number) => React.ReactNode;
  isRowClickable?: (rowOriginal: any) => boolean;
}
export const TanTableClient = ({
  data,
  columns,
  onRowClick,
  onEdit,
  onDelete,
  hidePagination = false,
  exportCsv = false,
  pageSize = 10,
  hideBorder = false,
  onPreview,
  onDuplicate,
  onSelectionChange,
  exportFileName = "table_data",
  selectOnClick = false,
  filterStorageKey,
  initialFilters,
  onClearFilters,
  initialSorting,
  renderSubRows,
  isRowClickable,
}: TanTableClientProps<any>) => {
  // Add filter state with persistence support
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    () => {
      // If we have a storage key, try to load saved filters
      if (filterStorageKey) {
        const savedFilters = localStorage.getItem(filterStorageKey);
        if (savedFilters) {
          try {
            return JSON.parse(savedFilters);
          } catch (e) {
            console.error("Failed to parse saved filters", e);
          }
        }
      }
      // Fall back to initialFilters or empty array
      return initialFilters ?? [];
    }
  );

  // Save filters to localStorage whenever they change
  React.useEffect(() => {
    if (filterStorageKey) {
      localStorage.setItem(filterStorageKey, JSON.stringify(columnFilters));
    }
  }, [columnFilters, filterStorageKey]);
  const isMobile = useIsMobile();
  // Not persisted: TanTableClient has no column chooser, so phones just drop the hideOnMobile columns.
  const columnVisibility = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.id, !(isMobile && col.hideOnMobile)])),
    [columns, isMobile],
  );
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    initialState: {
      pagination: {
        pageSize,
      },
      sorting: initialSorting ?? [],
    },
    // Add filter state and handler
    state: { columnFilters, columnVisibility },
    onColumnFiltersChange: setColumnFilters,
  });
  const [selectedRows, setSelectedRows] = React.useState<Set<any>>(new Set());
  const [headerRenderKey, setHeaderRenderKey] = React.useState(1);

  // Columns like the row-actions column (id "actions", used across the app for edit/delete/preview
  // buttons) are only meaningful on screen - exclude them from export/print output.
  const exportableColumns = React.useMemo(
    () => columns.filter((col: any) => col.id !== "actions"),
    [columns],
  );

  // Helper function to get export data based on selection
  const getExportData = (selectedOnly: boolean = false) => {
    if (selectedOnly && selectedRows.size > 0) {
      // Return only selected rows
      return table
        .getRowModel()
        .rows.filter((row) => selectedRows.has(row.original.id));
    }
    // Return all visible rows (current behavior)
    return table.getRowModel().rows;
  };

  // Helper function to get export filename with selection info
  const getExportFileName = (format: string, selectedOnly: boolean = false) => {
    const baseFileName = exportFileName;
    if (selectedOnly && selectedRows.size > 0) {
      return `${baseFileName} - zaznaczone (${selectedRows.size}).${format}`;
    }
    return `${baseFileName}.${format}`;
  };

  // Resolves a single cell's export value: prefers the column's getExportValue (needed when the
  // accessor holds a non-primitive, e.g. a tranche object, which would otherwise export as
  // "[object Object]"), then falls back to the raw accessor value with date/boolean formatting.
  const getCellExportValue = (col: (typeof columns)[number], rowOriginal: any) => {
    if (col.getExportValue) return col.getExportValue(rowOriginal) ?? "";

    // @ts-ignore
    const accessor = col.accessorKey as string;
    const value = rowOriginal?.[accessor];

    if (value instanceof Date) {
      return value.toLocaleDateString("pl-PL", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    // Only treat a string as a date if it's actually ISO-8601 shaped (what the API sends).
    // A generic Date.parse(value) check here previously misfired on plain text (e.g. names),
    // and Date.parse's lenient/non-standard fallback parsing differs across browser engines -
    // that's what made unrelated text randomly render as a date on some devices.
    if (typeof value === "string" && ISO_DATE_REGEX.test(value)) {
      const dateObj = new Date(value);
      if (!isNaN(dateObj.getTime())) {
        return dateObj.toLocaleDateString("pl-PL", {
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    }

    if (typeof value === "boolean") {
      return value ? "Tak" : "Nie";
    }

    if (typeof value === "object" && value !== null) return "";

    return value ?? "";
  };

  const buildExportRows = (selectedOnly: boolean) =>
    getExportData(selectedOnly).map((row) =>
      exportableColumns.map((col) => getCellExportValue(col, row.original)),
    );

  const handleExportCSV = (selectedOnly: boolean = false) => {
    const headers = exportableColumns.map((col) => col.header as string);
    const rows = buildExportRows(selectedOnly);
    const csv = Papa.unparse({ fields: headers, data: rows });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, getExportFileName("csv", selectedOnly));
  };

  const handleExportXLSX = (selectedOnly: boolean = false) => {
    const headers = exportableColumns.map((col) => col.header as string);
    const rows = buildExportRows(selectedOnly);
    const worksheet = XLSXUtils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSXUtils.book_new();
    XLSXUtils.book_append_sheet(workbook, worksheet, "Table Data");
    writeXLSXFile(workbook, getExportFileName("xlsx", selectedOnly));
  };

  const handleExportDOCX = async (selectedOnly: boolean = false) => {
    const rows = buildExportRows(selectedOnly);
    const docRows = rows.map(
      (row) =>
        new TableRow({
          children: row.map(
            (val) =>
              new TableCell({
                children: [new Paragraph(String(val))],
              })
          ),
        })
    );

    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph("Table Data"),
            new DocxTable({ rows: docRows }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, getExportFileName("docx", selectedOnly));
  };

  const handleExportPDF = (selectedOnly: boolean = false) => {
    const pdf = new jsPDF("landscape", "pt", "a4");
    pdf.setFont("Lato-Regular", "normal");

    const today = new Date();
    const exportedDate = today.toLocaleDateString("pl-PL", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    const headers = exportableColumns.map((col) => col.header as string);
    const rows = buildExportRows(selectedOnly);

    pdf.setFontSize(14);
    pdf.text(`Eksportowano: ${exportedDate}`, 40, 30);

    autoTable(pdf, {
      head: [headers],
      body: rows,
      startY: 50,
      styles: {
        font: "Lato-Regular",
        fontStyle: "normal",
        fontSize: 8,
      },
      headStyles: {
        font: "Lato-Regular",
        fontStyle: "normal",
        fontSize: 10,
        fillColor: [22, 160, 133],
        halign: "center",
      },
      margin: { horizontal: 10 },
      tableWidth: "auto",
      useCss: true,
    });

    pdf.save(getExportFileName("pdf", selectedOnly));
  };

  const handleCheckboxClick = (row: unknown) => {
    if (!hasObjectWithId(row)) {
      console.error("Row does not have id property");
      return;
    }

    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(row.original.id)) {
      newSelectedRows.delete(row.original.id);
    } else {
      newSelectedRows.add(row.original.id);
    }
    setSelectedRows(newSelectedRows);
    if (onSelectionChange) {
      onSelectionChange(Array.from(newSelectedRows));
    }
  };

  const handleRowClick = (rowOriginal: any, row?: any) => {
    // If selectOnClick is enabled and we have onSelectionChange, handle selection
    if (selectOnClick && onSelectionChange) {
      handleCheckboxClick({ original: rowOriginal });
    }

    // Always call the custom onRowClick if provided
    if (onRowClick) {
      onRowClick(rowOriginal, row);
    }
  };
  const handlePrint = (selectedOnly: boolean = false) => {
    const printData = getExportData(selectedOnly);
    const headers = exportableColumns.map((col) => col.header as string);

    // Create table HTML for printing
    const createPrintTable = () => {
      const headerRow = `<tr>${headers.map((header) => `<th>${header}</th>`).join("")}</tr>`;
      const bodyRows = printData
        .map(
          (row) =>
            `<tr>${exportableColumns
              .map((col) => `<td>${getCellExportValue(col, row.original)}</td>`)
              .join("")}</tr>`
        )
        .join("");

      return `
        <table>
          <thead>${headerRow}</thead>
          <tbody>${bodyRows}</tbody>
        </table>
      `;
    };

    const tableHTML = createPrintTable();
    const title =
      selectedOnly && selectedRows.size > 0
        ? `${exportFileName} - zaznaczone (${selectedRows.size})`
        : exportFileName;

    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              body { font-family: sans-serif; padding: 20px; }
              table { border-collapse: collapse; width: 100%; }
              th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
              thead { background-color: #f0f0f0; }
              h1 { color: #333; margin-bottom: 20px; }
            </style>
          </head>
          <body>
            <h1>${title}</h1>
            ${tableHTML}
            <script>
              window.onload = function () {
                window.print();
                window.onafterprint = function () {
                  window.close();
                };
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const borderClass =
    "rounded-xl border border-slate-200 bg-white p-4 shadow-lg dark:border-slate-700 dark:bg-slate-800";

  const anyColumnWithFilter = columns.some(
    (column) => column.enableColumnFilter
  );

  // Standard export actions for all data
  const getAllExportActions = () => [
    { name: "Eksport CSV", onClick: () => handleExportCSV(false) },
    { name: "Eksport PDF", onClick: () => handleExportPDF(false) },
    { name: "Eksport XLSX", onClick: () => handleExportXLSX(false) },
    // { name: "Export DOCX", onClick: handleExportDOCX },
    { name: "Drukuj tabelę", onClick: () => handlePrint(false) },
  ];

  // Export actions for selected data only
  const getSelectedExportActions = () => [
    { name: "CSV", onClick: () => handleExportCSV(true) },
    { name: "PDF", onClick: () => handleExportPDF(true) },
    { name: "XLSX", onClick: () => handleExportXLSX(true) },
    { name: "Drukuj", onClick: () => handlePrint(true) },
  ];

  return (
    <div className={`overflow-x-auto ${hideBorder ? "" : borderClass}`}>
      {(exportCsv || onClearFilters) && (
        <div className="p-2 flex justify-end gap-2">
          {exportCsv && (
            <>
              <MoreActionsMenu actions={getAllExportActions()}>
                Eksportuj
              </MoreActionsMenu>
              {selectedRows.size > 0 && (
                <MoreActionsMenu actions={getSelectedExportActions()}>
                  Eksportuj ({selectedRows.size} zaznaczonych)
                </MoreActionsMenu>
              )}
            </>
          )}
          {onClearFilters && (
            <PaziaButton
              type="tertiary"
              innerClassName="flex items-center gap-1"
              onClick={() => {
                table.resetColumnFilters();
                setHeaderRenderKey(Math.random());
                if (filterStorageKey) {
                  localStorage.removeItem(filterStorageKey);
                }
                onClearFilters();
              }}
            >
              <FaFilter className="text-slate-600" />
              Wyczyść
            </PaziaButton>
          )}
        </div>
      )}
      <table className="table-auto w-full dark:text-slate-300">
        <TableHeader
          table={table}
          setSelectedRows={setSelectedRows}
          key={`${headerRenderKey}-${isMobile}`}
          ignoreColumnSizes={isMobile}
          numberOfSelectedRows={selectedRows.size}
          onSelectionChange={onSelectionChange}
          renderFilters={anyColumnWithFilter}
        />
        <TableBody
          table={table}
          columns={columns}
          onRowClick={handleRowClick}
          onEdit={onEdit}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          onPreview={onPreview}
          selectedRows={selectedRows}
          onSelectionChange={onSelectionChange}
          handleCheckboxClick={handleCheckboxClick}
          renderSubRows={renderSubRows}
          isRowClickable={isRowClickable}
        />
      </table>
      {!hidePagination && (
        <Pagination
          table={table}
          sizes={TAN_TABLE_CONSTS.pageSizeOptions}
          isLoading={false}
          total={table.getPrePaginationRowModel().rows.length}
        />
      )}
    </div>
  );
};
